/**
 * IndexedDB 유틸 - 타임랩스 프레임 보관
 * DB: studyroom_timelapse, 버전 3
 * Store: frames (capturedAt 인덱스, 시간 범위 조회)
 *
 * 설계: 캡처 즉시 IDB 저장 → 시간 범위로 조회 → 24시간 후 자동 만료
 * 주의: iOS WebKit은 IDB에 Blob 직접 저장 시 읽기 오류 발생 → ArrayBuffer로 변환 저장
 */

const DB_NAME = 'studyroom_timelapse';
const DB_VERSION = 3;
const FRAMES_STORE = 'frames';
const EXPIRY_MS = 24 * 60 * 60 * 1000;

function openDB() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (e) => {
            const db = e.target.result;

            // v1/v2 → v3: Blob → ArrayBuffer 저장 방식 변경
            if (db.objectStoreNames.contains(FRAMES_STORE)) {
                db.deleteObjectStore(FRAMES_STORE);
            }
            if (db.objectStoreNames.contains('videos')) {
                db.deleteObjectStore('videos');
            }

            const store = db.createObjectStore(FRAMES_STORE, { autoIncrement: true });
            store.createIndex('capturedAt', 'capturedAt', { unique: false });
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

/**
 * 단일 프레임 즉시 저장 (캡처 시점에 호출)
 * Blob → ArrayBuffer 변환 후 저장 (iOS 호환)
 */
async function appendFrame(blob, timerInfo = null) {
    const buffer = await blob.arrayBuffer();
    const db = await openDB();
    const tx = db.transaction(FRAMES_STORE, 'readwrite');
    tx.objectStore(FRAMES_STORE).add({
        data: buffer,
        mimeType: blob.type || 'image/jpeg',
        timerInfo,
        capturedAt: Date.now()
    });
    return new Promise((resolve, reject) => {
        tx.oncomplete = () => { db.close(); resolve(); };
        tx.onerror = () => { db.close(); reject(tx.error); };
    });
}

/**
 * 시간 범위로 프레임 조회 (타임랩스 생성 시 사용)
 * ArrayBuffer → Blob 복원하여 반환
 */
async function getFramesByTimeRange(startTime, endTime) {
    const db = await openDB();
    const tx = db.transaction(FRAMES_STORE, 'readonly');
    const index = tx.objectStore(FRAMES_STORE).index('capturedAt');
    const range = IDBKeyRange.bound(startTime, endTime);
    const request = index.getAll(range);

    return new Promise((resolve, reject) => {
        request.onsuccess = () => {
            db.close();
            const frames = (request.result || []).map(row => ({
                blob: new Blob([row.data], { type: row.mimeType || 'image/jpeg' }),
                timerInfo: row.timerInfo,
                capturedAt: row.capturedAt
            }));
            resolve(frames);
        };
        request.onerror = () => { db.close(); reject(request.error); };
    });
}

/**
 * 시간 범위 내 프레임 존재 여부 (히스토리 아이템에서 사용)
 */
async function hasFramesByTimeRange(startTime, endTime) {
    const db = await openDB();
    const tx = db.transaction(FRAMES_STORE, 'readonly');
    const index = tx.objectStore(FRAMES_STORE).index('capturedAt');
    const range = IDBKeyRange.bound(startTime, endTime);
    const request = index.count(range);

    return new Promise((resolve, reject) => {
        request.onsuccess = () => { db.close(); resolve(request.result > 0); };
        request.onerror = () => { db.close(); reject(request.error); };
    });
}

/**
 * 24시간 이전 프레임 삭제
 */
async function cleanupExpiredFrames() {
    const db = await openDB();
    const cutoff = Date.now() - EXPIRY_MS;
    const tx = db.transaction(FRAMES_STORE, 'readwrite');
    const index = tx.objectStore(FRAMES_STORE).index('capturedAt');
    const range = IDBKeyRange.upperBound(cutoff);
    const request = index.openCursor(range);

    return new Promise((resolve, reject) => {
        request.onsuccess = (e) => {
            const cursor = e.target.result;
            if (!cursor) return;
            cursor.delete();
            cursor.continue();
        };
        tx.oncomplete = () => { db.close(); resolve(); };
        tx.onerror = () => { db.close(); reject(tx.error); };
    });
}

export {
    appendFrame,
    getFramesByTimeRange,
    hasFramesByTimeRange,
    cleanupExpiredFrames
};
